import { MessageInput } from "@chatscope/chat-ui-kit-react";
import { Send2, Shop } from "iconsax-react";
import { AiOutlinePaperClip } from "react-icons/ai";
import { useEffect, useRef, useState } from "react";
import {
  appStore,
  chatState,
  chatStore,
  chatUserStore,
  fileStore,
  userStore,
} from "../zustand/index";
import { isEmpty } from "underscore";
import Order from "./components/OrderModal";
import { DateTime } from "luxon";
import DOMPurify from "dompurify";
import he from "he";
import EmojiPicker from "emoji-picker-react";
import { BsEmojiSmile } from "react-icons/bs";
import { useChatManager } from "../hooks/useChatManager";
import ImagePreview from "./ImagePreview";
import { useFileManager } from "../hooks/useFileManager";
import { LoadingButton } from "@mui/lab";
import { InstantDb } from "../zustand/instant.schema";
import { Box, Button, useTheme } from "@mui/material";
import { ChatMode } from "../types/chatmode.enum";
import { useChannel } from "ably/react";
import { NotificationChannel } from "../zustand/const";
import { Notify } from "notiflix";
import { handleError } from "../util/functions";
import { AppNameSpaces, AppRooms } from "../types/app-rooms.enum";



function ChatInput({ className = "", instanceMode = ChatMode.FULL }) {
  const theme = useTheme();
  const appMode = appStore((state) => state.mode);
  const fp = appStore((state) => state.fp);
  const user = userStore((state) => state.user);

  const typedMessage = chatStore((state) => state.typedMessage);
  const setTypedMessage = chatStore((state) => state.setTypedMessage);
  const [enableSend, setEnableSend] = useState(false);
  const [isImageMode, setIsImageMode] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { createMessage } = useChatManager();

  const roomId = chatState((state) => state.roomId);

  const {
    setFirstName,
    setLastName,
    setEmail,
  } = chatUserStore();

  const [activeKey, setActiveKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const files = fileStore((state) => state.files);
  const addFiles = fileStore((state) => state.addFiles);
  const previewFiles = fileStore((state) => state.previewFiles);
  // const isAppInView = useAppVisibility();
  const [filesInfo, setFilesInfo] = useState([])
  const { handleFiles } = useFileManager();
  const roomInfo = chatState((state) => state.roomInfo);

  const room = InstantDb.room(AppRooms.CHAT, roomId)
  const { inputProps } = InstantDb.rooms.useTypingIndicator(
    room,
    AppNameSpaces.CHAT_INPUT,
  );

  const { channel } = useChannel(NotificationChannel, 'message', () => {
  });

  const formatMessage = (
    message,
    messageType,
    roomIdValue
  ) => {
    if (!isImageMode) {
      const msg = {
        id: "",
        identifier: "",
        roomId: roomIdValue ? roomIdValue : roomId,
        senderType:
          appMode === "admin"
            ? 'admin'
            : 'customer',
        isDelivered: true,
        isRead: false,
        isSaved: false,
        isDeleted: false,
        isEdited: false,
        editedAt: null,
        deletedAt: null,
        content: message,
        messageType: messageType,
        sender: user && 'id' in user ? user.id : fp,
        createdAt: new Date().toISOString(),
      };
      return msg;
    } else {
      const msg = {
        id: activeKey,
        content: typedMessage,
        identifier: "",
        roomId: roomId,
        senderType:
          appMode === "admin"
            ? 'admin'
            : 'customer',
        messageType: 'document',
        meta: null,
        editedAt: null,
        deletedAt: null,
        sender: user && 'id' in user ? user.id : fp,
        isDelivered: false,
        isRead: false,
        isSaved: false,
        isDeleted: false,
        isEdited: false,
        createdAt: new Date().toISOString(),
      };
      return msg;
    }
  };

  const scrollToBottom = () => {
    const chatContainer = document.querySelector('.chat-box .scrollbar-container');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }

  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (files.length <= 0) {
      return;
    }
    const key = "file-" + Date.now();
    setFilesInfo(files)
    addFiles(key, files);
    setActiveKey(key);
    setIsImageMode(true);
  };

  const sendMessage = async (msg) => {
    const reset = () => {
      setTypedMessage('')
      setIsImageMode(false);
      fileInputRef.current.value = "";
    }

    try {
      setIsLoading(true)
      let result = null;
      let message = null
      if (!isImageMode) {
        message = formatMessage(msg, 'text');
        await createMessage(message).then((res) => {
          reset()
          result = res
        });
      } else {
        const fileData = handleFiles(filesInfo)
        message = formatMessage(msg, 'document');
        message.meta = fileData
        await createMessage(message, true)
          .then((res) => {
            reset()
            result = res
            // console.log('message: ', message);
          }).finally(() => {
          });

      }

      if (result.status === 'synced') {
        handleNotification(message)
      }
      //
      scrollToBottom();

      return result
    } catch (error) {
      handleError(error);
      console.error("Error sending message:", error);
      return null;
    } finally {
      setIsLoading(false)
    }
  };

  const sendOrder = async (id) => {
    createMessage({
      id: "",
      content: id,
      identifier: "",
      messageType: 'order',
      roomId,
      senderType: 'admin',
      isDeleted: false,
      isDelivered: false,
      isEdited: false,
      editedAt: null,
      deletedAt: null,
      isRead: false,
      isSaved: false,
      sender: user.email,
      createdAt: DateTime.now().toUTC().toISO(),
    });
  };

  const handleNotification = (message) => {
    const data = {
      intent: 'chat',
      type: message?.messageType,
      content: message.content,
      customer: roomInfo.customer,
      senderType: message.senderType,
      sender: message.sender,
      roomId: message.roomId,
      raw: message
    }
    channel.publish('message', data)
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roomId) {
      console.error('User room not found');
      return;
    }
    if (isEmpty(typedMessage) && !isImageMode) {
      return;
    }
    let msg = he.decode(typedMessage);
    msg = msg.trim();
    msg = DOMPurify.sanitize(msg);

    await sendMessage(msg)
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  useEffect(() => {
    if (!isEmpty(user) && user.email) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (isEmpty(files)) {
      setIsImageMode(false);
    }
  }, [files]);

  useEffect(() => {
    const m = typedMessage.replace(/<br\s*\/?>/gi, "").trim();
    if (!isEmpty(previewFiles)) {
      setEnableSend(true);
    } else if (!isEmpty(m)) {
      setEnableSend(true);
    } else {
      setEnableSend(false);
    }
  }, [typedMessage, previewFiles]);

  const handleChange = (value) => {
    setTypedMessage(value);
  };

  const handleClickOutside = (event) => {
    if (showEmojiPicker && !event.target.closest('.emoji-picker') && !event.target.closest('.emoji-button')) {
      setShowEmojiPicker(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showEmojiPicker]);

  return (
    <>
      <form
        action="#"
        onSubmit={handleSubmit}
        className={`chat-input mx-auto flex items-center mb-0 w-[94%] sm:w-[90%] max-w-[800px] ${className} rounded-sm ${isImageMode ? "image-mode" : ""
          }`}
      >
        <div className="emoji-picker w-[90%] mx-auto max-w-[300px]">
          {showEmojiPicker && (
            <EmojiPicker
              onEmojiClick={(emoji) => {
                setTypedMessage(typedMessage + emoji.emoji);
              }}
              width={"100%"}
              height={400}
              previewConfig={{
                showPreview: false,
              }}
            />
          )}
        </div>
        <div className="flex gap-1">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple
          />
          <Box className="" sx={{
            display: "flex",
            alignItems: "center",
            gap: "5px",

          }}>
            <Button
              variant="text"
              type="button"
              onClick={() => {
                fileInputRef.current.click();
              }}
              tabIndex={0}
              className=""
            >
              <AiOutlinePaperClip className="h-3 w-3 sm:h-6 sm:w-6" />
            </Button>
            {appMode === "admin" && (
              <>
              </>
            )}
          </Box>
        </div>
        <Box display="flex" sx={{
          display: 'flex',
          width: 'auto',
          flex: 1,
          flexDirection: 'column',
        }} className="flex flex-col w-auto flex-1 text-input">
          {isImageMode && <ImagePreview activeKey={activeKey} />}
          <MessageInput
            className="chat-input-box"
            autoFocus
            placeholder={isImageMode ? "Add a caption" : "Type a message..."}
            value={typedMessage}
            onChange={handleChange}
            onKeyUp={(e) => {
              if (roomId) {
                inputProps.onKeyDown(e)
              }
            }}
            onBlur={() => {
              if (roomId) {
                inputProps.onBlur()
              }
            }}
            attachButton={false}
            onKeyDown={handleKeyDown}
            sendButton={false}
          />
        </Box>
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={'3px'}>
          {instanceMode === ChatMode.FULL && <>
            <Button
              variant="text"
              onClick={(e) => {
                e.preventDefault();
                setShowEmojiPicker(!showEmojiPicker);
              }}
              className="btn btn-text btn-sm xs:btn-md input-btn border-none emoji-button"
            >
              <BsEmojiSmile className="h-6 w-6" />
            </Button>
          </>}
          <LoadingButton
            sx={{ p: 1 }}
            loading={isLoading}
            data-name={"submit-button"}
            type="submit"
            className={`btn rounded-full btn-sm xs:btn-md ${!enableSend ? "disabled" : ""
              }`}
            disabled={!enableSend}
          >
            <Box sx={{
              [theme.breakpoints.down('sm')]: {
                display: 'none'
              },
              [theme.breakpoints.up('sm')]: {
                display: 'block'
              }
            }} className="">Send</Box>
            <Send2 className="h-6 w-6" />
          </LoadingButton>
        </Box>

      </form>
      <Order callback={sendOrder} context={"chat"} mode={"create"} />
    </>
  );
}
export default ChatInput;
